import dayjs from "dayjs";

import {
  QueryModelPosition,
  QueryProfile,
  QuerySecurity,
} from "@/api/queries/types";
import { Contact, Portfolio } from "@/api/types";
import { BuyOrderRow } from "@/forms/forms/orders/orderForm/components/BuyOrders";
import { SellOrderRow } from "@/forms/forms/orders/orderForm/components/SellOrders";
import {
  computeSuitabilityDocParams,
  getModelStockFraction,
} from "@/forms/forms/quality/suitability/SuitabilityForm.rules";
import { PendingOrder } from "@/forms/forms/quality/suitability/SuitabilityForm.types";
import { DocProducerDocument } from "@/views/docproducer";

// ---------------------------------------------------------------------------
// Output type
// ---------------------------------------------------------------------------

export type ProductionDocument = {
  /** Source document id */
  id: string;
  /** docType of the source document */
  type: string;
  /** PDF template filename */
  docName: string;
  /** Template fill parameters */
  params: Record<string, string | object | unknown[]>;
};

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

type FeeContext = {
  agreedFee: number;
  accountFee: number;
  totCost: number;
  manFee: number;
  kickback: number;
};

type OrderSuitData = {
  suitportId: string;
  suitportValue: string;
  suitportSubTotFee: string;
  suitportSubInvFee: string;
  suitportCost: string;
  suitportTotFee: string;
  suitportSP: string;
  hasOrders: boolean;
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** 2 decimal places, Norwegian locale (space thousands, comma decimal) */
function df(value: number): string {
  return value.toLocaleString("nb-NO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** 0 decimal places, Norwegian locale */
function dfzero(value: number): string {
  return value.toLocaleString("nb-NO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/** Parse any value to float; returns 0 for falsy/NaN */
function dd(val: unknown): number {
  if (val === null || val === undefined || val === "") return 0;
  if (typeof val === "number") return isNaN(val) ? 0 : val;
  return parseFloat(String(val).replace(",", ".")) || 0;
}

/** Safe string coercion */
function cleanVal(val: unknown): string {
  if (val === null || val === undefined) return "";
  return String(val);
}

/** Format Norwegian bank account number as xxxx xx xxxxx */
function fmtAccount(val: string): string {
  const digits = val.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 6)} ${digits.slice(6)}`;
  }
  return val;
}

/** Current date as DD.MM.YYYY */
function today(): string {
  return dayjs().format("DD.MM.YYYY");
}

// ---------------------------------------------------------------------------
// Document name mapping
// ---------------------------------------------------------------------------

function getDocumentName(docType: string, isJuridical: boolean): string {
  switch (docType) {
    case "meetingMinutes":
      return "docMeetReport.pdf";
    case "suitabilityStatement":
      return isJuridical ? "docSuitedNewOrg.pdf" : "docSuitedNew.pdf";
    case "docSuitabilityNew":
      return isJuridical ? "docSuitedNewOrg.pdf" : "docSuitedNew.pdf";
    case "customerAgreement":
      return isJuridical ? "docCustAgreeOrg.pdf" : "docCustAgree.pdf";
    case "openCustomerInstruction":
      return "capOpenDoc.pdf";
    case "advisoryAgainst":
      return "docReject.pdf";
    default:
      return `${docType}.pdf`;
  }
}

// ---------------------------------------------------------------------------
// Model metadata
// ---------------------------------------------------------------------------

const MODEL_DESCRIPTIONS: Record<string, string> = {
  NORDNET: "Nordnet",
  CAPGUARDRENTER: "Aktiv Renter",
  CAPGUARDTRYGG: "Trygg",
  CAPGUARDEKB: "Aktiv Egenkapitalbevis",
  CAPGUARDEIENDOM: "Aktiv Eiendom",
  FONDSOBLIGASJON: "Fondsobligasjoner",
  CAPGUARDAKSJER: "Aktiv Aksjer",
  CAPGUARD75: "Aktiv 75/25",
  CAPGUARD60: "Aktiv 60/40",
  CAPGUARD45: "Aktiv 45/55",
  CAPGUARD25: "Aktiv 25/75",
};

function getModelDescription(modelType: string): string {
  return MODEL_DESCRIPTIONS[modelType] ?? modelType;
}

function getModelRisk(modelType: string): string {
  let retVal = "";
  if (modelType.includes("AKSJER")) retVal = "\n\n\n\n\n\nX";
  if (modelType.includes("75")) retVal = "\n\n\n\n\nX";
  if (modelType.includes("60")) retVal = "\n\n\n\nX";
  if (modelType.includes("45")) retVal = "\n\n\nX";
  if (modelType.includes("25")) retVal = "\n\nX";
  if (modelType.includes("RENTER")) retVal = "\nX";
  if (modelType.includes("TRYGG")) retVal = "X";
  return retVal;
}

function getModelListPrice(amount: number, modelType: string): number {
  if (modelType === "NORDNET") return 0.6;
  if (modelType === "CAPGUARDTRYGG") return 0.25;
  if (modelType === "FONDSOBLIGASJON") return 0.5;
  if (modelType === "CAPGUARDRENTER") {
    if (amount < 2_000_000) return 1.0;
    if (amount < 5_000_000) return 0.8;
    if (amount < 10_000_000) return 0.7;
    return 0.5;
  }
  if (modelType === "CAPGUARDEKB" || modelType === "CAPGUARDEIENDOM") {
    if (amount < 2_000_000) return 1.2;
    if (amount < 5_000_000) return 1.1;
    if (amount < 10_000_000) return 1.0;
    return 0.9;
  }
  // Default: AKSJER, CAPGUARD25/45/60/75
  if (amount < 2_000_000) return 1.2;
  if (amount < 5_000_000) return 1.0;
  if (amount < 10_000_000) return 0.9;
  return 0.8;
}

function getPortCostinfo(amount: number, modelType: string): string {
  const b0 = df(getModelListPrice(0, modelType));
  const b2 = df(getModelListPrice(2_000_000, modelType));
  const b5 = df(getModelListPrice(5_000_000, modelType));
  const b10 = df(getModelListPrice(10_000_000, modelType));
  const cur =
    amount < 2_000_000
      ? 0
      : amount < 5_000_000
      ? 1
      : amount < 10_000_000
      ? 2
      : 3;
  const mark = (i: number) => (i === cur ? " ←" : "");
  return [
    `Under 2 mill: ${b0} %${mark(0)}`,
    `2 - 5 mill: ${b2} %${mark(1)}`,
    `5 - 10 mill: ${b5} %${mark(2)}`,
    `Over 10 mill: ${b10} %${mark(3)}`,
  ].join("\n");
}

// ---------------------------------------------------------------------------
// Agreement document filenames
// ---------------------------------------------------------------------------

function getAgreementDocName(modelType: string): string {
  if (modelType === "CAPGUARDEKB") return "capAktivEKB.pdf";
  if (modelType === "CAPGUARDEIENDOM") return "capAktivEiendom.pdf";
  return "capAktivFund.pdf";
}

function getAccountAgreementDocName(portType: string): string {
  if (portType === "20") return "capFundAgree.pdf";
  if (portType === "21") return "capVPSAgree.pdf";
  if (portType === "22") return "capAskAgree.pdf";
  if (portType === "23" || portType === "24") return "capIPSAgree.pdf";
  return "capFundAgree.pdf";
}

// ---------------------------------------------------------------------------
// Standard params (contact + advisor data)
// ---------------------------------------------------------------------------

function getStdParams(
  contact: Contact,
  avdData: Contact | null | undefined
): Record<string, string> {
  const addr =
    contact.primaryAddress ?? contact.addresses?.[0] ?? ({} as never);
  // juridical.id === 2 = natural person; !== 2 = legal entity / company
  const isJuridical = contact.juridical.id !== 2;

  const conZip = cleanVal(addr.zipCode);
  const conCity = cleanVal(addr.city);
  const conName = cleanVal(contact.name);

  const advAddr =
    avdData?.primaryAddress ?? avdData?.addresses?.[0] ?? ({} as never);
  const curCity = cleanVal(advAddr.city || "Oslo");

  // PoA representative: first rep whose contactId starts with "F"
  const poaRep = contact.representatives?.find((r) =>
    r.contactId?.startsWith("F")
  );
  const conPoA = poaRep ? cleanVal(poaRep.name) : !isJuridical ? conName : "";

  // Name split for persons ("Lastname, Firstname" convention in some systems)
  const nameParts = conName.includes(",")
    ? conName.split(",").map((s) => s.trim())
    : [conName, ""];
  const conLastName = !isJuridical ? nameParts[0] : "";
  const conFirstName = !isJuridical ? nameParts[1] ?? "" : "";

  const curDate = today();
  const curPlaceDate = `${curCity}, ${curDate}`;
  const conCityDate = `${conCity}, ${curDate}`;

  return {
    conName,
    conAddress: cleanVal(addr.address1),
    conCountry: cleanVal(
      contact.taxCountry?.name ?? contact.nationality?.name ?? ""
    ),
    conCitizenship: cleanVal(contact.nationality?.name ?? ""),
    conCity,
    conZip,
    conZipCity: `${conZip} ${conCity}`,
    conMail: cleanVal(addr.email),
    conPhone: cleanVal(addr.phone1),
    conDbId: String(contact.id),
    conType: cleanVal(contact.juridical?.code),
    conId: cleanVal(contact.contactId).replace("F", ""),
    conCityDate,
    conLastName,
    conFirstName,
    conPoA,
    conLei: cleanVal(contact.profile?.lei?.value),
    curUser: cleanVal(avdData?.name),
    curUserConId: cleanVal(avdData?.contactId),
    curUserMail: cleanVal(advAddr.email),
    curUserPlace: curCity,
    curDate,
    curPlaceDate,
    // Hardcoded Capguard company details
    curComp: "Capguard Asset Management AS",
    curCompId: "990 310 505",
    curCompAddress: "Haakon VIIs gate 1",
    curCompZip: "0161",
    curCompCity: "Oslo",
    curCompCountry: "Norge",
    curCompLei: "254900BYEZ88HZQOU884",
    curCompNN: "Capguard // Rådgiver",
    curCompPhone: "22 59 22 50",
    curCompMail: "post@capguard.no",
    curCompNNsigned:
      cleanVal(avdData?.name) + " // Capguard Asset Management AS",
    prodTime: dayjs().format("DD.MM.YYYY HH:mm"),
  };
}

// ---------------------------------------------------------------------------
// Fee calculation
// ---------------------------------------------------------------------------

/**
 * Mutates `params` with fee-related keys and updates `ctx` with computed values.
 * Pass `actMandat = ""` for non-model (management agreement) context.
 */
function setFeesAsParam(
  params: Record<string, string>,
  faModels: QueryModelPosition[],
  actMandat: string,
  fee: number,
  amount: number,
  ctx: FeeContext,
  isCap: boolean
): void {
  const accountFee = isCap ? 0.15 : 0.3;
  // 1.5 is a sentinel meaning "use list price" — resolve to the actual list price
  const effectiveFee = fee === 1.5 ? getModelListPrice(amount, actMandat) : fee;
  ctx.agreedFee = effectiveFee;
  ctx.accountFee = accountFee;

  if (actMandat && actMandat !== "" && actMandat !== "NOMODEL") {
    // Weighted-average fund fees across model positions
    const modelPositions = faModels.filter((p) => p.short_name === actMandat);
    let totalManFee = 0;
    let totalKick = 0;
    const totalShares = modelPositions.reduce(
      (s, p) => s + p.share_of_portfolio,
      0
    );
    for (const pos of modelPositions) {
      const share = totalShares > 0 ? pos.share_of_portfolio / totalShares : 0;
      totalManFee += pos.management_fee_percentage * share;
      totalKick +=
        (pos.kickback_percentage / 100) * pos.management_fee_percentage * share;
    }

    const r = (n: number) => Math.round(n * 100) / 100;
    params.portCost =
      df(effectiveFee) +
      " %" +
      (accountFee > 0 ? ` + ${df(accountFee)} % i kontoavgift` : "");
    params.portClean = df(r(effectiveFee) - r(totalKick)) + " %";
    params.portManFee = df(totalManFee) + " %";
    params.portManKick = df(totalKick) + " %";
    params.portTotFee =
      df(r(effectiveFee) + r(totalManFee) + r(accountFee) - r(totalKick)) +
      " %";
    params.actAccountFee = df(accountFee) + " %";

    ctx.totCost = effectiveFee;
    ctx.manFee = totalManFee - totalKick;
    ctx.kickback = totalKick;

    const info2Parts = modelPositions[0]?.info2?.split(";") ?? [];
    const perfFeeRate = info2Parts[1] ?? "";
    params.actPerfFee = perfFeeRate ? perfFeeRate + " %" : "";

    const listPrice = getModelListPrice(amount, actMandat);
    if (effectiveFee >= listPrice) {
      params.portCostInfo = getPortCostinfo(amount, actMandat);
    }
  } else {
    // No model — management agreement
    params.portCost = df(effectiveFee) + " %";
    params.portCostType = isCap ? "Capguardhonoraret" : "Honorar";
    ctx.totCost = effectiveFee;
    ctx.manFee = 0;
    ctx.kickback = 0;
  }
}

// ---------------------------------------------------------------------------
// Per-order document generation
// ---------------------------------------------------------------------------

function findDocsNeededForOrder(
  doc: DocProducerDocument,
  baseParams: Record<string, string>,
  faModels: QueryModelPosition[],
  faSec: QuerySecurity[]
): { docs: ProductionDocument[]; suitData: OrderSuitData } {
  const raw = doc.data as Record<string, unknown>;
  const newData = (raw?.newData ?? raw) as Record<string, unknown>;
  const existData = (raw?.existData ?? {}) as Record<string, unknown>;

  const portId = cleanVal(newData.dpPortfolioId);
  const portName = cleanVal(
    newData.dpPortfolioName || existData.dpPortfolioName || "Portefølje"
  );
  const portShortName = cleanVal(
    existData.dpPortfolioShortName || newData.dpPortfolioShortName || ""
  );
  const portType = cleanVal(newData.dpPortType || existData.dpPortType || "");
  const modelType = cleanVal(
    newData.dpModelType || existData.dpModelType || "NOMODEL"
  );

  const isExisting =
    portId !== "NEW" &&
    portId !== "" &&
    existData.isExisting !== false &&
    cleanVal(existData.isExisting) !== "false";

  const prevModelType = cleanVal(existData.dpModelType || "");
  const newFee = dd(newData.dpManagementFee);
  const prevFee = dd(existData.dpManagementFee);

  const dpAmountToInvest = dd(newData.dpAmountToInvest);
  const dpSumAvCash = dd(existData.dpSumAvCash ?? newData.dpSumAvCash);
  const dpSumInsValue = dd(existData.dpSumInsValue ?? newData.dpSumInsValue);
  const dpOnOffAmount = dd(newData.dpOnOffAmount);
  const dpWithdrawal = dd(newData.dpWithdrawal);
  const dpOnOffDate = cleanVal(newData.dpOnOffDate);
  const dpAccount = fmtAccount(
    cleanVal(newData.dpAccount || newData.dpNewAccount || "")
  );

  const buyOrders = (newData.buyOrders as BuyOrderRow[]) || [];
  const sellOrders = (newData.sellOrders as SellOrderRow[]) || [];

  const portAmount = dpSumAvCash + dpSumInsValue + dpAmountToInvest;
  const isCap = ["20", "21", "22", "23", "24"].includes(portType);
  const isModel = modelType !== "NOMODEL" && modelType !== "";
  const accountFee = isCap ? 0.15 : 0.3;
  const feeChanged = prevFee !== newFee;

  const acAgreeNeeded =
    (!isExisting && isCap) ||
    (feeChanged && !isModel) ||
    (modelType !== prevModelType && modelType === "NOMODEL");

  const modAgreeNeeded =
    (feeChanged && isModel) || (isModel && modelType !== prevModelType);

  const docs: ProductionDocument[] = [];
  const ctx: FeeContext = {
    agreedFee: 0,
    accountFee,
    totCost: 0,
    manFee: 0,
    kickback: 0,
  };

  const portTypeLabels: Record<string, string> = {
    "20": "Fondskonto",
    "21": "VPS-konto",
    "22": "ASK",
    "23": "IPS",
    "24": "IPS",
  };
  const displayPortName = !isExisting
    ? `${portTypeLabels[portType] ?? "Portefølje"} Ny`
    : portName;

  // For savings/withdrawal docs: "Fondskonto - 20-12379878"
  const portIdLabel =
    isExisting && portShortName
      ? `${portTypeLabels[portType] ?? "Portefølje"} - ${portShortName}`
      : displayPortName;

  // ── Account agreement ────────────────────────────────────────────────────
  if (acAgreeNeeded) {
    const acDocName = getAccountAgreementDocName(portType);
    const acParams: Record<string, string> = { ...baseParams };
    acParams.portId = displayPortName;
    acParams.portAccount = dpAccount;
    setFeesAsParam(acParams, faModels, "", newFee, portAmount, ctx, isCap);
    docs.push({
      id: doc.id,
      type: "accountAgreement",
      docName: acDocName,
      params: acParams,
    });
    docs.push({
      id: doc.id,
      type: "terms",
      docName: "terms.pdf",
      params: { ...baseParams },
    });

    if (!isModel) {
      const manParams: Record<string, string> = { ...baseParams, ...acParams };
      manParams.portId = displayPortName;
      manParams.portAmount = dfzero(portAmount) + " kr";
      manParams.portCostType = isCap ? "Capguardhonoraret" : "Honorar";
      docs.push({
        id: doc.id,
        type: "managementAgreement",
        docName: "capManAgreement.pdf",
        params: manParams,
      });
    }
  }

  // ── Model agreement ──────────────────────────────────────────────────────
  if (modAgreeNeeded) {
    const modDocName = getAgreementDocName(modelType);
    const modParams: Record<string, string> = { ...baseParams };
    modParams.portId = displayPortName;
    modParams.portAccount = dpAccount;
    modParams.actMandat = getModelDescription(modelType);
    modParams.actAmount =
      dfzero(portAmount) +
      " kr" +
      `, nytt volum: ${dfzero(dpAmountToInvest)} kr`;
    modParams.actDesc =
      faModels.find((p) => p.short_name === modelType)?.description ??
      getModelDescription(modelType);
    modParams.actRisk = getModelRisk(modelType);
    setFeesAsParam(
      modParams,
      faModels,
      modelType,
      newFee,
      portAmount,
      ctx,
      isCap
    );
    docs.push({
      id: doc.id,
      type: "modelAgreement",
      docName: modDocName,
      params: modParams,
    });
  }

  // ── One-off savings ──────────────────────────────────────────────────────
  if (dpOnOffAmount > 0) {
    const onOffParams: Record<string, string> = { ...baseParams };

    const descPortfolio =
      displayPortName + " " + (isExisting ? portShortName : "");
    const descOrderType = isModel
      ? getModelDescription(modelType)
      : "Manuell ordre";

    onOffParams.onOffAmt = dfzero(dpOnOffAmount) + " kr";
    onOffParams.onOffDesc = descPortfolio + "\n" + descOrderType;
    onOffParams.portOnOffDate = dpOnOffDate || "";
    onOffParams.portAccount = dpAccount;
    onOffParams.portId = displayPortName;
    docs.push({
      id: doc.id,
      type: "oneOff",
      docName: "capOnOff.pdf",
      params: onOffParams,
    });
  }

  // ── Withdrawal ───────────────────────────────────────────────────────────
  if (dpWithdrawal > 0) {
    const wdParams: Record<string, string> = { ...baseParams };
    wdParams.wdAmount = dfzero(dpWithdrawal) + " kr";
    wdParams.productionDokName = "Uttaksblankett";
    wdParams.wdAcc = dpAccount;
    wdParams.portName = portName;
    docs.push({
      id: doc.id,
      type: "withdrawal",
      docName: "capStdWd.pdf",
      params: wdParams,
    });
  }

  // ── Manual orders (buy / sell / model) ───────────────────────────────────
  const hasBuyOrders = buyOrders.some(
    (o) => o.securityId !== null && o.securityId !== 0
  );
  const hasSellOrders = sellOrders.some((o) => o.securityId !== 0);

  if (hasBuyOrders || hasSellOrders) {
    const orderParams: Record<string, string> = { ...baseParams };
    orderParams.portAccount = dpAccount;
    let count = 1;

    for (const order of buyOrders) {
      if (!order.securityId) continue;
      const secDet = faSec.find((s) => s.id === order.securityId);
      const secCode =
        secDet?.isin_code ?? order.isinCode ?? order.instrumentCode;
      orderParams[`sec${count}`] = `${order.instrumentName}\n${secCode}`;
      orderParams[`type${count}`] = "Kjøp";
      orderParams[`mt${count}`] =
        order.type === "Limit" ? `Limit ${order.limitPrice}` : "Market";
      if (dd(order.amountInput) > 0) {
        orderParams[`totAmount${count}`] =
          dfzero(dd(order.amountInput)) + " kr";
      } else if (dd(order.quantity) > 0) {
        orderParams[`amount${count}`] = dfzero(dd(order.quantity)) + " stk";
      }
      count++;
    }

    for (const order of sellOrders) {
      if (!order.securityId) continue;
      orderParams[`sec${count}`] = `${order.name}\n${order.securityCode}`;
      orderParams[`type${count}`] = "Salg";
      orderParams[`mt${count}`] =
        order.type === "Limit" ? `Limit ${order.limitPrice}` : "Market";
      orderParams[`amount${count}`] = dfzero(dd(order.sellQty)) + " stk";
      count++;
    }

    if (isModel) {
      const modelPositions = faModels.filter((p) => p.short_name === modelType);
      if (
        modelType === "CAPGUARDEKB" ||
        modelType === "CAPGUARDEIENDOM" ||
        modelType === "FONDSOBLIGASJON" ||
        modelPositions.length <= 1
      ) {
        const desc = getModelDescription(modelType);
        orderParams[`sec${count}`] = `${desc}\n${modelType}`;
        orderParams[`type${count}`] = "Kjøp";
        orderParams[`mt${count}`] = "Rebalansering";
        orderParams[`totAmount${count}`] = dfzero(portAmount) + " kr";
        count++;
      } else {
        for (const pos of modelPositions) {
          const posAmount = portAmount * (pos.share_of_portfolio / 100);
          const posName = pos.name ?? pos.description ?? pos.short_name;
          const posCode = pos.isin_code ?? pos.security_code ?? pos.short_name;
          orderParams[`sec${count}`] = `${posName}\n${posCode}`;
          orderParams[`type${count}`] = "Kjøp";
          orderParams[`mt${count}`] = "Rebalansering";
          orderParams[`totAmount${count}`] = dfzero(posAmount) + " kr";
          count++;
        }
      }
    }

    if (count > 1) {
      docs.push({
        id: doc.id,
        type: "manualOrder",
        docName: "manOrder.pdf",
        params: orderParams,
      });
    }
  }

  // ── Savings agreement ─────────────────────────────────────────────────────
  const dpSavingsEnabled = newData.dpSavingsEnabled === true;
  if (dpSavingsEnabled) {
    const dpSavingsAmount = dd(newData.dpSavingsAmount);
    const dpSavingsDayOfMonth = cleanVal(newData.dpSavingsDayOfMonth);
    const dpSavingsAvtalegiro = newData.dpSavingsAvtalegiro === true;
    const dpSavingsDayNum = dd(newData.dpSavingsDayOfMonth);

    let savOrderDate: string;
    if (isModel) {
      savOrderDate = "Handles i henhold til mandat";
    } else if (dpSavingsDayNum < 9 || dpSavingsDayNum > 24) {
      savOrderDate = "10";
    } else {
      savOrderDate = "25";
    }

    const savParams: Record<string, string> = {
      ...baseParams,
      portId: portIdLabel,
      portAccount: dpAccount,
      savAmount: dfzero(dpSavingsAmount) + " kr",
      savDate: dpSavingsDayOfMonth,
      savAG: dpSavingsAvtalegiro ? "Ja" : "Nei",
      savOrderDate,
    };

    if (!isModel) {
      const dpSavingsFunds =
        (newData.dpSavingsFunds as Array<{ name: string; pct: number }>) || [];
      dpSavingsFunds.slice(0, 5).forEach((fund, i) => {
        const n = i + 1;
        const pct = dd(fund.pct);
        const fundKr = Math.round((pct / 100) * dpSavingsAmount);
        savParams[`fund${n}Name`] = cleanVal(fund.name);
        savParams[`amount${n}`] =
          "kr " + dfzero(fundKr) + " (" + dfzero(pct) + " %)";
      });
    } else {
      // Explicitly clear all fund fields so the template doesn't carry over old values
      for (let n = 1; n <= 5; n++) {
        savParams[`fund${n}Name`] = "";
        savParams[`amount${n}`] = "";
      }
    }

    docs.push({
      id: doc.id + "_sav",
      type: "savingsOrder",
      docName: "capSavOrder.pdf",
      params: savParams,
    });
  }

  // ── Withdrawal agreement ──────────────────────────────────────────────────
  const dpWithdrawalEnabled = newData.dpWithdrawalEnabled === true;
  if (dpWithdrawalEnabled) {
    const dpWdAmount = dd(newData.dpWdAmount);
    const dpWdDayOfMonth = cleanVal(newData.dpWdDayOfMonth);
    const dpWdAccount = fmtAccount(cleanVal(newData.dpWdAccount || ""));

    const wdAgreementParams: Record<string, string> = {
      ...baseParams,
      portId: portIdLabel,
      portWdAccount: dpWdAccount,
      portFixedWdDate: dpWdDayOfMonth,
      portFixedWdAmount: dfzero(dpWdAmount) + " kr",
    };

    docs.push({
      id: doc.id + "_wd",
      type: "withdrawalOrder",
      docName: "capFixedWd.pdf",
      params: wdAgreementParams,
    });
  }

  // ── Suitability data for this order ──────────────────────────────────────
  const suitPortAmount = portAmount - dpWithdrawal;

  let suitportSP = "";
  if (isModel) {
    const stockFrac = getModelStockFraction(modelType, faModels);
    suitportSP = df(stockFrac * 100) + " %";
  }

  const suitCtx: FeeContext = {
    agreedFee: 0,
    accountFee,
    totCost: 0,
    manFee: 0,
    kickback: 0,
  };
  const suitFeeParams: Record<string, string> = {};
  setFeesAsParam(
    suitFeeParams,
    faModels,
    isModel ? modelType : "",
    newFee,
    suitPortAmount,
    suitCtx,
    isCap
  );

  const hasOrders =
    hasBuyOrders ||
    hasSellOrders ||
    isModel ||
    dpAmountToInvest > 0 ||
    dpOnOffAmount > 0;

  const suitData: OrderSuitData = {
    suitportId: `${portName}${
      isModel ? " " + getModelDescription(modelType) : ""
    }`,
    suitportValue: dfzero(suitPortAmount) + " kr",
    suitportSubTotFee: suitFeeParams.portTotFee ?? df(suitCtx.manFee) + " %",
    suitportSubInvFee:
      df(suitCtx.agreedFee - suitCtx.accountFee - suitCtx.manFee) + " %",
    suitportCost: suitFeeParams.portCost ?? df(newFee) + " %",
    suitportTotFee:
      df(suitCtx.agreedFee + suitCtx.manFee + suitCtx.kickback) + " %",
    suitportSP,
    hasOrders,
  };

  return { docs, suitData };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Translates the queued DocProducer documents into a flat list of
 * `ProductionDocument` items that map 1-to-1 with PDF templates.
 *
 * Mirrors the logic of the Flowable `AutoProdScript` Groovy script.
 */
export function buildDocumentProductionList(
  contact: Contact,
  avdData: Contact | null | undefined,
  documents: DocProducerDocument[],
  faModels: QueryModelPosition[],
  faSec: QuerySecurity[],
  profileData: QueryProfile[],
  activePorts: Portfolio[],
  pendingOrders: PendingOrder[]
): ProductionDocument[] {
  const isJuridical = contact.juridical.id !== 2;
  const baseParams = getStdParams(contact, avdData);
  const result: ProductionDocument[] = [];

  const suitNewDoc = documents.find((d) => d.docType === "docSuitabilityNew");

  for (const doc of documents) {
    switch (doc.docType) {
      case "order": {
        const { docs } = findDocsNeededForOrder(
          doc,
          baseParams,
          faModels,
          faSec
        );
        result.push(...docs);
        break;
      }
      case "meetingMinutes": {
        const mmRaw = doc.data as Record<string, unknown>;
        const mmData = (mmRaw?.newData ?? mmRaw) as Record<string, unknown>;
        const mmParams: Record<string, string> = { ...baseParams };
        for (const [key, val] of Object.entries(mmData)) {
          if (val === null || val === undefined || typeof val === "object")
            continue;
          mmParams[key] = String(val);
        }
        mmParams.agentInfo = "";
        result.push({
          id: doc.id,
          type: doc.docType,
          docName: getDocumentName(doc.docType, isJuridical),
          params: mmParams,
        });
        break;
      }

      case "openCustomerInstruction": {
        const ociRaw = doc.data as Record<string, unknown>;
        const ociData = (ociRaw?.newData ?? ociRaw) as Record<string, unknown>;
        const ociParams: Record<string, string> = { ...baseParams };
        for (const [key, val] of Object.entries(ociData)) {
          if (val === null || val === undefined || typeof val === "object")
            continue;
          ociParams[key] = String(val);
        }
        ociParams.productionType = "Åpen kundeinstruks";
        ociParams.productionDokName = "Åpen kundeinstruks";
        result.push({
          id: doc.id,
          type: doc.docType,
          docName: getDocumentName(doc.docType, isJuridical),
          params: ociParams,
        });
        break;
      }
      case "advisoryAgainst": {
        const aaRaw = doc.data as Record<string, unknown>;
        const aaData = (aaRaw?.newData ?? aaRaw) as Record<string, unknown>;
        const aaParams: Record<string, string> = { ...baseParams };
        for (const [key, val] of Object.entries(aaData)) {
          if (val === null || val === undefined || typeof val === "object")
            continue;
          aaParams[key] = String(val);
        }
        aaParams.productionType = "Frarådning";
        aaParams.productionDokName = "Frarådning";
        result.push({
          id: doc.id,
          type: doc.docType,
          docName: getDocumentName(doc.docType, isJuridical),
          params: aaParams,
        });
        break;
      }
      case "uploaded":
        result.push({
          id: doc.id,
          type: doc.docType,
          docName: doc.desc || "vedlegg.pdf",
          params: { ...doc.data },
        });
        break;
      case "documentFromSecurity": {
        const dfsRaw = doc.data as {
          fileName?: string;
          documentType?: string;
          docStamperData?: Record<string, unknown>;
        };
        const dfsParams: Record<string, string> = {};
        for (const [key, val] of Object.entries(dfsRaw.docStamperData ?? {})) {
          if (val === null || val === undefined || typeof val === "object")
            continue;
          dfsParams[key] = String(val);
        }
        result.push({
          id: doc.id,
          type: doc.docType,
          docName: dfsRaw.fileName || "dokument.pdf",
          params: { ...baseParams, ...dfsParams },
        });
        break;
      }
      case "suitabilityStatement":
        // Handled below so order suitport data is available
        break;
      case "docSuitabilityNew":
        // Handled below so order suitport data is available
        break;
    }
  }

  // ── docSuitabilityNew — uses stored SuitStatementData directly ───────────
  if (suitNewDoc) {
    const nd = ((suitNewDoc.data as Record<string, unknown> | undefined)
      ?.newData ?? {}) as Record<string, unknown>;

    // Spread ALL primitive fields from nd as strings (covers everything AutoSuit.toData() returns).
    // Arrays are kept as-is (not stringified) so the backend receives them as real arrays.
    const allNdAsStrings: Record<string, string | unknown[]> = {};
    for (const [key, value] of Object.entries(nd)) {
      if (value == null) continue;
      if (Array.isArray(value)) {
        allNdAsStrings[key] = value; // keep as real array
      } else if (typeof value === "object") {
        allNdAsStrings[key] = JSON.stringify(value);
      } else {
        allNdAsStrings[key] = String(value);
      }
    }

    const newParams: Record<string, string | unknown[]> = {
      ...baseParams,
      ...allNdAsStrings,
      // Override: advisor name with fallback between the two field names
      AdvisorName: String(nd["AdvisorName"] ?? nd["advisors"] ?? ""),
      advisors: String(nd["advisors"] ?? nd["AdvisorName"] ?? ""),
    };

    result.push({
      id: suitNewDoc.id,
      type: suitNewDoc.docType,
      docName: "docSuitabilityNew.pdf",
      params: newParams,
    });
  }

  return result;
}
